import { useState } from 'react'
import { router, usePage } from '@inertiajs/react'
import { MoreHorizontal, Mail, Clock, UserX, RefreshCw, Shield, ShieldCheck, Crown } from 'lucide-react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { SearchInput } from '@/components/shared/SearchInput'
import { Pagination, PaginationMeta } from '@/components/shared/Pagination'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback } from '@/components/ui/avatar'
import { InviteMemberDialog } from './InviteMemberDialog'
import { formatDate } from '@/lib/utils'
import { DashboardPageProps, Member, Invite } from '@/types'

type MembersIndexProps = {
  members: Member[]
  invites: Invite[]
  search: string
  pagination: PaginationMeta
}

type ConfirmDialogState = {
  open: boolean
  title: string
  description: string
  onConfirm: () => void
}

function getRoleBadge(role: string) {
  switch (role) {
    case 'owner':
      return <Badge variant="default" className="gap-1 text-[11px]"><Crown className="h-3 w-3" />Owner</Badge>
    case 'admin':
      return <Badge variant="secondary" className="gap-1 text-[11px]"><ShieldCheck className="h-3 w-3" />Admin</Badge>
    default:
      return <Badge variant="outline" className="gap-1 text-[11px]"><Shield className="h-3 w-3" />Member</Badge>
  }
}

function getInitials(name: string) {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export default function MembersIndex({ members, invites, search, pagination }: MembersIndexProps) {
  const { auth, sidebar } = usePage<DashboardPageProps>().props
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [confirmDialog, setConfirmDialog] = useState<ConfirmDialogState>({
    open: false,
    title: '',
    description: '',
    onConfirm: () => {},
  })

  const accountSlug = auth.account?.slug
  const canManageMembers = sidebar?.permissions?.can_manage_members
  const canChangeRoles = auth.user?.role === 'owner'

  const closeConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }))
  }

  const handleChangeRole = (memberId: string, newRole: string) => {
    router.patch(`/${accountSlug}/members/${memberId}`, { role: newRole }, {
      preserveScroll: true,
    })
  }

  const handleRemoveMember = (memberId: string, memberName: string) => {
    setConfirmDialog({
      open: true,
      title: 'Remove Member',
      description: `Are you sure you want to remove ${memberName} from this workspace? This action cannot be undone.`,
      onConfirm: () => {
        router.delete(`/${accountSlug}/members/${memberId}`, {
          preserveScroll: true,
        })
        closeConfirmDialog()
      },
    })
  }

  const handleCancelInvite = (inviteId: string, inviteEmail: string) => {
    setConfirmDialog({
      open: true,
      title: 'Cancel Invitation',
      description: `Are you sure you want to cancel the invitation to ${inviteEmail}?`,
      onConfirm: () => {
        router.delete(`/${accountSlug}/invites/${inviteId}`, {
          preserveScroll: true,
        })
        closeConfirmDialog()
      },
    })
  }

  const handleResendInvite = (inviteId: string) => {
    router.post(`/${accountSlug}/invites/${inviteId}/resend`, {}, {
      preserveScroll: true,
    })
  }

  return (
    <DashboardLayout title="Members">
      <div className="space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold">Members</h1>
            <p className="text-[13px] text-muted-foreground mt-1">
              Manage who has access to this workspace
            </p>
          </div>
          {canManageMembers && (
            <Button onClick={() => setInviteDialogOpen(true)} size="sm" className="h-8 text-[13px]">
              <Mail className="mr-1.5 h-3.5 w-3.5" />
              Invite Member
            </Button>
          )}
        </div>

        <SearchInput value={search} placeholder="Search members..." />

        {/* Team Members */}
        <div>
          <div className="mb-3">
            <h2 className="text-sm font-semibold text-foreground">Team Members</h2>
            <p className="text-[12px] text-muted-foreground mt-0.5">
              {pagination.total} member{pagination.total !== 1 ? 's' : ''} in this workspace
            </p>
          </div>

          {/* Header row */}
          <div className="flex items-center px-3 py-1.5 text-[12px] font-medium text-muted-foreground uppercase tracking-wider">
            <div className="flex-1">Member</div>
            <div className="w-28">Role</div>
            <div className="w-32">Joined</div>
            {canManageMembers && <div className="w-10" />}
          </div>

          {/* Member rows */}
          <div>
            {members.map((member) => (
              <div
                key={member.id}
                className="flex items-center px-3 py-2.5 rounded-md hover:bg-muted/50 transition-colors group"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="text-[11px]">{getInitials(member.name)}</AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium truncate">{member.name}</div>
                    <div className="text-[12px] text-muted-foreground truncate">{member.email_address}</div>
                  </div>
                </div>
                <div className="w-28">{getRoleBadge(member.role)}</div>
                <div className="w-32 text-[13px] text-muted-foreground">
                  {formatDate(member.created_at)}
                </div>
                {canManageMembers && (
                  <div className="w-10 flex justify-end">
                    {member.id !== auth.user?.id && member.role !== 'owner' && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Actions</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          {canChangeRoles && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() => handleChangeRole(member.id, member.role === 'admin' ? 'member' : 'admin')}
                              >
                                {member.role === 'admin' ? 'Demote to Member' : 'Promote to Admin'}
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleRemoveMember(member.id, member.name)}
                          >
                            <UserX className="mr-2 h-4 w-4" />
                            Remove Member
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>

          <Pagination pagination={pagination} />
        </div>

        {/* Pending Invitations */}
        {invites.length > 0 && (
          <div>
            <div className="mb-3">
              <h2 className="text-sm font-semibold text-foreground">Pending Invitations</h2>
              <p className="text-[12px] text-muted-foreground mt-0.5">
                {invites.length} pending invitation{invites.length !== 1 ? 's' : ''}
              </p>
            </div>

            {/* Header row */}
            <div className="flex items-center px-3 py-1.5 text-[12px] font-medium text-muted-foreground uppercase tracking-wider">
              <div className="flex-1">Email</div>
              <div className="w-28">Role</div>
              <div className="w-32">Invited By</div>
              <div className="w-32">Expires</div>
              {canManageMembers && <div className="w-10" />}
            </div>

            {/* Invite rows */}
            <div>
              {invites.map((invite) => (
                <div
                  key={invite.id}
                  className="flex items-center px-3 py-2.5 rounded-md hover:bg-muted/50 transition-colors group"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="h-7 w-7 rounded-full bg-muted flex items-center justify-center shrink-0">
                      <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    </div>
                    <span className="text-[13px] font-medium truncate">{invite.email}</span>
                  </div>
                  <div className="w-28">
                    <Badge variant="outline" className="capitalize text-[11px]">{invite.role}</Badge>
                  </div>
                  <div className="w-32 text-[13px] text-muted-foreground">{invite.inviter_name}</div>
                  <div className="w-32 text-[13px] text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {formatDate(invite.expires_at)}
                    </div>
                  </div>
                  {canManageMembers && (
                    <div className="w-10 flex justify-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreHorizontal className="h-4 w-4" />
                            <span className="sr-only">Actions</span>
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleResendInvite(invite.id)}>
                            <RefreshCw className="mr-2 h-4 w-4" />
                            Resend Invitation
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => handleCancelInvite(invite.id, invite.email)}
                          >
                            <UserX className="mr-2 h-4 w-4" />
                            Cancel Invitation
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <InviteMemberDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
        />

        <AlertDialog open={confirmDialog.open} onOpenChange={(open) => !open && closeConfirmDialog()}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{confirmDialog.title}</AlertDialogTitle>
              <AlertDialogDescription>{confirmDialog.description}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDialog.onConfirm}>
                Continue
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </DashboardLayout>
  )
}
